import { Children, useCallback, useLayoutEffect, useRef, useState } from 'react';
import '../../assets/styles/pedometer.scss';

const PANEL_STORAGE_KEY = 'playground-hero-panel';

function readStoredPanel(count) {
  try {
    // 앱 재실행에도 유지 (sessionStorage는 WebView 종료 시 소실)
    let raw = Number(localStorage.getItem(PANEL_STORAGE_KEY));
    if (!Number.isFinite(raw)) {
      const legacy = sessionStorage.getItem(PANEL_STORAGE_KEY);
      if (legacy != null) {
        raw = Number(legacy);
        if (Number.isFinite(raw) && raw >= 0 && raw < count) {
          localStorage.setItem(PANEL_STORAGE_KEY, String(raw));
        }
      }
    }
    return Number.isFinite(raw) && raw >= 0 && raw < count ? raw : 0;
  } catch (error) {
    return 0;
  }
}

function PlaygroundHeroCarousel({ children, height, aspectRatio = '360 / 432', onIndexChange }) {
  const panels = Children.toArray(children);
  const scrollerRef = useRef(null);
  const [index, setIndex] = useState(() => readStoredPanel(panels.length));
  /** 초기 스크롤 복원이 끝나기 전 onScroll이 index를 0으로 덮어쓰지 않게 */
  const restoreDoneRef = useRef(false);

  const persistIndex = useCallback((next) => {
    try {
      localStorage.setItem(PANEL_STORAGE_KEY, String(next));
    } catch (error) {
      // private mode 등
    }
  }, []);

  const scrollToIndex = useCallback((panelIndex) => {
    const scroller = scrollerRef.current;
    if (!scroller) return false;
    const width = scroller.clientWidth;
    if (!width) return false;
    const target = panelIndex * width;
    if (Math.abs(scroller.scrollLeft - target) > 1) {
      scroller.scrollLeft = target;
    }
    return true;
  }, []);

  // 마지막 패널로 스크롤 복원 (레이아웃 width가 잡힐 때까지 재시도)
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const targetIndex = index;
    persistIndex(targetIndex);

    if (targetIndex === 0) {
      restoreDoneRef.current = true;
      return undefined;
    }

    let cancelled = false;
    let tries = 0;

    const tryRestore = () => {
      if (cancelled) return;
      if (scrollToIndex(targetIndex)) {
        restoreDoneRef.current = true;
        return;
      }
      tries += 1;
      if (tries < 30) {
        window.requestAnimationFrame(tryRestore);
      } else {
        // width를 끝내 못 잡아도 이후 스크롤은 허용
        restoreDoneRef.current = true;
      }
    };

    tryRestore();

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (!restoreDoneRef.current) {
              tryRestore();
            } else if (targetIndex > 0) {
              // 회전·리사이즈 후에도 위치 유지
              scrollToIndex(targetIndex);
            }
          })
        : null;
    ro?.observe(scroller);

    return () => {
      cancelled = true;
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    onIndexChange?.(index);
  }, [index, onIndexChange]);

  const onScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !scroller.clientWidth) return;
    // 복원 중이면 무시 (scrollLeft=0 이벤트가 index를 지움)
    if (!restoreDoneRef.current) return;

    const next = Math.round(scroller.scrollLeft / scroller.clientWidth);
    setIndex((current) => {
      if (current === next) return current;
      persistIndex(next);
      return next;
    });
  }, [persistIndex]);

  const scrollerStyle = height != null ? { height } : { aspectRatio, width: '100%' };

  return (
    <div className="relative w-full">
      <div
        ref={scrollerRef}
        className="pdm-carousel flex w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden"
        style={scrollerStyle}
        onScroll={onScroll}
      >
        {panels.map((panel, panelIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={panelIndex} className="relative h-full w-full min-w-full shrink-0 snap-center">
            {panel}
          </div>
        ))}
      </div>

      {panels.length > 1 && (
        <div className="pointer-events-none absolute bottom-5 left-1/2 z-[7] flex -translate-x-1/2 items-center gap-1.5">
          {panels.map((panel, panelIndex) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={panelIndex}
              className={`h-1.5 rounded-full transition-all ${
                panelIndex === index ? 'w-4 bg-white' : 'w-1.5 bg-black/25'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default PlaygroundHeroCarousel;
